import React from 'react';
import { Table } from 'antd';
import numeral from 'numeral';
import _ from 'lodash';
import { type FinancesTableRow, type FinancesFilters } from '../../../../types/finances';
import { getColumnVisibility } from '../utils/columnUtils';
import { toNumber } from '../../../../helpers/number';

interface FinancesSummaryProps {
  data: FinancesTableRow[];
  filters: FinancesFilters;
}

export const FinancesSummary: React.FC<FinancesSummaryProps> = ({ data, filters }) => {
  // Calculate totals
  const totalSspRequests = _.sumBy(data, (row) => toNumber(row.sspRequests));
  const totalDspRequests = _.sumBy(data, (row) => toNumber(row.dspRequests));
  const totalBids = _.sumBy(data, (row) => toNumber(row.bids));
  const totalImpressions = _.sumBy(data, (row) => toNumber(row.impressions));
  const totalNurl = _.sumBy(data, (row) => toNumber(row.nurl));
  const totalBurl = _.sumBy(data, (row) => toNumber(row.burl));
  const totalSspSpend = _.sumBy(data, (row) => toNumber(row.sspSpend));
  const totalDspSpend = _.sumBy(data, (row) => toNumber(row.dspSpend));
  const totalProfit = _.sumBy(data, (row) => toNumber(row.profit));

  // Weighted coverage by SSP requests so single-row matches and multi-row reflects mix
  const weightedCoverageNumerator = _.sumBy(data, (row) => (row.coverage || 0) * toNumber(row.sspRequests));
  const overallCoverage = totalSspRequests > 0 ? weightedCoverageNumerator / totalSspRequests : undefined;
  const overallWinRate = totalBids > 0 ? totalImpressions / totalBids : undefined;

  // Use shared column visibility logic — pass data so hiddenByResponse reflects actual API response
  const columnVisibility = getColumnVisibility(filters, data);
  const { hiddenByResponse } = columnVisibility;

  // Totals for extra columns
  const extraTotals: Record<string, number> = {};
  columnVisibility.extraColsColumns.forEach((field) => {
    extraTotals[field] = _.sumBy(data, (row) => toNumber(row[field]));
  });

  let columnIndex = 0;

  return (
    <Table.Summary.Row style={{ fontWeight: 'bold' }}>
      <Table.Summary.Cell index={columnIndex++} />
      {columnVisibility.dateColumn && !hiddenByResponse.has('datetime') && <Table.Summary.Cell index={columnIndex++} />}
      {columnVisibility.sspColumn && !hiddenByResponse.has('sspId') && <Table.Summary.Cell index={columnIndex++} />}
      {columnVisibility.dspColumn && !hiddenByResponse.has('dspId') && <Table.Summary.Cell index={columnIndex++} />}

      {/* Dynamic groupBy columns */}
      {columnVisibility.dynamicGroupByColumns
        .filter((field) => !hiddenByResponse.has(field))
        .map((field) => (
          <Table.Summary.Cell key={field} index={columnIndex++} />
        ))}

      {/* Core metric totals — omit cells for columns the API dropped */}
      {!hiddenByResponse.has('sspRequests') && (
        <Table.Summary.Cell index={columnIndex++} align="right">
          {numeral(totalSspRequests).format('0,0')}
        </Table.Summary.Cell>
      )}

      {!hiddenByResponse.has('dspRequests') && (
        <Table.Summary.Cell index={columnIndex++} align="right">
          {numeral(totalDspRequests).format('0,0')}
        </Table.Summary.Cell>
      )}

      {/* Coverage overall (already a percent number) */}
      {!hiddenByResponse.has('coverage') && (
        <Table.Summary.Cell index={columnIndex++} align="right">
          {overallCoverage !== undefined ? `${numeral(overallCoverage).format('0.00')}%` : '-'}
        </Table.Summary.Cell>
      )}

      {/* Win Rate overall */}
      {!hiddenByResponse.has('winRate') && (
        <Table.Summary.Cell index={columnIndex++} align="right">
          {overallWinRate !== undefined ? numeral(overallWinRate).format('0.00%') : '-'}
        </Table.Summary.Cell>
      )}

      {!hiddenByResponse.has('bids') && (
        <Table.Summary.Cell index={columnIndex++} align="right">
          {numeral(totalBids).format('0,0')}
        </Table.Summary.Cell>
      )}

      {!hiddenByResponse.has('impressions') && (
        <Table.Summary.Cell index={columnIndex++} align="right">
          {numeral(totalImpressions).format('0,0')}
        </Table.Summary.Cell>
      )}

      {!hiddenByResponse.has('nurl') && (
        <Table.Summary.Cell index={columnIndex++} align="right">
          {numeral(totalNurl).format('0,0')}
        </Table.Summary.Cell>
      )}

      {!hiddenByResponse.has('burl') && (
        <Table.Summary.Cell index={columnIndex++} align="right">
          {numeral(totalBurl).format('0,0')}
        </Table.Summary.Cell>
      )}

      {!hiddenByResponse.has('sspSpend') && (
        <Table.Summary.Cell index={columnIndex++} align="right">
          {numeral(totalSspSpend).format('$0,0.000')}
        </Table.Summary.Cell>
      )}

      {!hiddenByResponse.has('dspSpend') && (
        <Table.Summary.Cell index={columnIndex++} align="right">
          {numeral(totalDspSpend).format('$0,0.000')}
        </Table.Summary.Cell>
      )}

      {!hiddenByResponse.has('profit') && (
        <Table.Summary.Cell index={columnIndex++} align="right">
          {numeral(totalProfit).format('$0,0.000')}
        </Table.Summary.Cell>
      )}

      {/* Extra columns totals (after Profit to match table order) */}
      {columnVisibility.extraColsColumns
        .filter((field) => !hiddenByResponse.has(field))
        .map((field) => (
          <Table.Summary.Cell key={`extra-${field}`} index={columnIndex++} align="right">
            {numeral(extraTotals[field] || 0).format('0,0')}
          </Table.Summary.Cell>
        ))}
    </Table.Summary.Row>
  );
};
